/**
 * TIP Shell — District Console: SCHOOLS (migration 0016; trusted chrome, parent DOM only).
 *
 * The district says which school each class belongs to, and how that school looks: a name,
 * a mascot, two colours. That is what apps receive as `init.theme` and what the Teacher
 * Console shows in its header. Presentation ONLY — the section says so, because a district
 * admin should never wonder whether "school" changes who can see what. (It does not.)
 *
 * The colour rule is shown BEFORE saving, with a live preview: both colours sit under
 * white text in the apps, and one that is too light is refused by the server (and would
 * otherwise be silently dropped by every app). The preview uses the same 4.5:1 arithmetic
 * the server and the apps use; the server remains the authority.
 */
import { useEffect, useState } from 'preact/hooks';
import type { ServerClient, School, SchoolsView } from './api.js';

const HEX = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;

function contrastOnWhite(hex: string): number {
  const full = hex.length === 4 ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}` : hex;
  const ch = (at: number) => {
    const v = parseInt(full.slice(at, at + 2), 16) / 255;
    return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  return 1.05 / (0.2126 * ch(1) + 0.7152 * ch(3) + 0.0722 * ch(5) + 0.05);
}
const readable = (hex: string) => HEX.test(hex) && contrastOnWhite(hex) >= 4.5;

type Draft = { id?: string; name: string; mascot: string; primary: string; accent: string };
const BLANK: Draft = { name: '', mascot: '', primary: '#2f5fd0', accent: '#0a7d5c' };

export function SchoolsSection({ api, onChanged }: { api: ServerClient; onChanged(): void }) {
  const [view, setView] = useState<SchoolsView | null>(null);
  const [unavailable, setUnavailable] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [classMsg, setClassMsg] = useState<Record<string, string>>({});

  const load = () => {
    api.adminSchools().then((v) => { setView(v); setUnavailable(null); })
      .catch((e: unknown) => setUnavailable(/503/.test(String(e))
        ? 'Schools are not set up on this server yet.'
        : 'The school list could not load.'));
  };
  useEffect(load, [api]);

  const save = async () => {
    if (!draft) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await api.saveSchool(draft);
      if (r.ok) {
        setDraft(null);
        setMsg(`Saved “${r.school.name}”. Apps in its classes use this look from their next launch.`);
        load();
        onChanged();
      } else {
        setMsg(`⛔ ${r.reason}`);
      }
    } catch (e) {
      setMsg(`⛔ The school was not saved: ${e instanceof Error ? e.message : String(e)}`);
    }
    setBusy(false);
  };

  const assign = async (classId: string, schoolId: string) => {
    setClassMsg((m) => ({ ...m, [classId]: 'Saving…' }));
    try {
      const r = await api.setClassSchool(classId, schoolId === '' ? null : schoolId);
      setClassMsg((m) => ({ ...m, [classId]: r.ok ? 'Saved.' : `⛔ ${r.reason ?? 'Not saved.'}` }));
      if (r.ok) { load(); onChanged(); }
    } catch (e) {
      setClassMsg((m) => ({ ...m, [classId]: `⛔ ${e instanceof Error ? e.message : String(e)}` }));
    }
  };

  return (
    <section class="section-gap schools">
      <h2 class="section-title">Schools</h2>
      <p class="grant-line">
        Which school each class belongs to, and how that school looks in its apps: name, mascot
        and two colours. This changes how apps look — never what they can see.
      </p>

      {unavailable ? <p class="notice-line">{unavailable}</p> : view === null ? (
        <p style="color:var(--ink-faint);">Loading schools…</p>
      ) : (
        <>
          {view.schools.length === 0 && draft === null ? (
            <p style="color:var(--ink-soft);">No schools yet. Add one, then choose it for its classes below.</p>
          ) : null}
          {view.schools.map((s) => (
            <SchoolRow key={s.id} school={s} disabled={busy || draft !== null}
              onEdit={() => { setMsg(null); setDraft({ ...s }); }} />
          ))}

          {draft ? (
            <SchoolForm draft={draft} busy={busy} onChange={setDraft} onSave={() => void save()}
              onCancel={() => { setDraft(null); setMsg(null); }} />
          ) : (
            <div class="ledger-line">
              <button type="button" class="step" onClick={() => { setMsg(null); setDraft({ ...BLANK }); }}>
                Add a school
              </button>
            </div>
          )}
          {msg ? <div class={`action-result settle${msg.startsWith('⛔') ? ' err' : ''}`}>{msg}</div> : null}

          {view.classes.length > 0 && view.schools.length > 0 ? (
            <div class="school-classes">
              <h3 class="minor-head">Classes</h3>
              {view.classes.map((c) => (
                <div class="ledger-line" key={c.classId}>
                  <label for={`school-of-${c.classId}`}>{c.name}</label>
                  <span class="push">
                    <select id={`school-of-${c.classId}`} class="field" value={c.schoolId ?? ''}
                      onChange={(e) => void assign(c.classId, (e.currentTarget as HTMLSelectElement).value)}>
                      <option value="">No school (built-in look)</option>
                      {view.schools.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                    {classMsg[c.classId] ? (
                      <span class={`class-school-msg${classMsg[c.classId]!.startsWith('⛔') ? ' err' : ''}`} role="status">
                        {classMsg[c.classId]}
                      </span>
                    ) : null}
                  </span>
                </div>
              ))}
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

function SchoolRow({ school, disabled, onEdit }: { school: School; disabled: boolean; onEdit(): void }) {
  return (
    <div class="ledger-line school-row">
      <span class="swatches" aria-hidden="true">
        <span class="swatch" style={`background:${school.primary}`} />
        <span class="swatch" style={`background:${school.accent}`} />
      </span>
      <span><strong>{school.name}</strong>{school.mascot ? <span class="mascot"> · {school.mascot}</span> : null}</span>
      <span class="push">
        <button type="button" class="quiet" disabled={disabled} onClick={onEdit}>Edit</button>
      </span>
    </div>
  );
}

function SchoolForm(props: {
  draft: Draft; busy: boolean; onChange(d: Draft): void; onSave(): void; onCancel(): void;
}) {
  const { draft, busy } = props;
  const set = (k: keyof Draft) => (e: Event) => props.onChange({ ...draft, [k]: (e.currentTarget as HTMLInputElement).value });
  const okP = readable(draft.primary);
  const okA = readable(draft.accent);
  const ok = draft.name.trim().length > 0 && okP && okA;
  return (
    <div class="school-form settle">
      <label class="compose-label" for="school-name">School name</label>
      <input id="school-name" class="field" maxLength={80} value={draft.name} disabled={busy} onInput={set('name')} />
      <label class="compose-label" for="school-mascot">Mascot <span class="opt">(optional)</span></label>
      <input id="school-mascot" class="field" maxLength={40} value={draft.mascot} disabled={busy} onInput={set('mascot')} />

      <div class="colour-pair">
        {([['primary', 'Main colour', okP], ['accent', 'Second colour', okA]] as const).map(([k, label, good]) => (
          <div class="colour" key={k}>
            <label class="compose-label" for={`school-${k}`}>{label}</label>
            <span class="colour-inputs">
              <input type="color" aria-label={`${label} picker`} value={HEX.test(draft[k]) && draft[k].length === 7 ? draft[k] : '#000000'}
                disabled={busy} onInput={set(k)} />
              <input id={`school-${k}`} class="field hex" value={draft[k]} disabled={busy} onInput={set(k)} />
            </span>
            {!good ? <span class="colour-warn">Too light for white text — pick a darker shade.</span> : null}
          </div>
        ))}
      </div>

      {/* What students will see: white text on each colour, exactly as the apps use them. */}
      <div class="school-preview" aria-hidden="true">
        <span class="pv-btn" style={`background:${HEX.test(draft.primary) ? draft.primary : '#999'}`}>Start</span>
        <span class="pv-btn" style={`background:${HEX.test(draft.accent) ? draft.accent : '#999'}`}>✓ Correct</span>
        <span class="pv-school">{draft.name || 'Your school'}{draft.mascot ? ` · ${draft.mascot}` : ''}</span>
      </div>

      <div class="compose-act">
        <button type="button" class="act" disabled={busy || !ok} onClick={props.onSave}>
          {busy ? 'Saving…' : draft.id ? 'Save changes' : 'Add school'}
        </button>
        <button type="button" class="quiet" disabled={busy} onClick={props.onCancel}>cancel</button>
      </div>
    </div>
  );
}
